import * as linkifyjs from "linkifyjs";

/**
 * Converts URLs in text to clickable links
 *
 * @param {string} content - The text content that may contain URLs
 * @param {boolean} addLineBreaks - Whether to replace newlines with <br> tags (default: true)
 * @returns {object} - Object containing the HTML string with linkified content
 */
/**
 * Linkifies a single string (no newlines). Used to avoid linkifyjs matching
 * across <br> when multiple URLs are on separate lines.
 *
 * @param {string} segment One line of text.
 * @returns {string} HTML with anchors.
 */
function linkifySegment(segment) {
  const linkified = linkifyjs.find(segment).reduce(
    (acc, match) => {
      const before = segment.substring(acc.lastIndex, match.start);
      const href = match.href || `https://${match.value}`;
      const link = `<a href="${href}" target="_blank" rel="noopener noreferrer" class="text-blue-500 underline hover:text-blue-700">${match.value}</a>`;
      acc.html += before + link;
      acc.lastIndex = match.end;
      return acc;
    },
    { html: "", lastIndex: 0 }
  );
  linkified.html += segment.substring(linkified.lastIndex);
  return linkified.html;
}

/**
 * Convert URLs in plain text to anchor HTML. Split on newlines when
 * `addLineBreaks` so stacked URLs do not concatenate into one href.
 *
 * @param {string} content
 * @param {boolean} [addLineBreaks=true]
 * @returns {{html: string}}
 */
export const linkifyText = (content, addLineBreaks = true) => {
  if (!content) return { html: "" };

  if (addLineBreaks) {
    // Linkify each line separately so multiple URLs on separate lines become
    // separate links instead of one concatenated URL (e.g. url1<br>url2<br>url3).
    const lines = content.split(/\n/);
    const html = lines.map((line) => linkifySegment(line)).join("<br>");
    return { html };
  }

  const html = linkifySegment(content);
  return { html };
};

/**
 * React component helper that returns a React element with linkified content
 *
 * @param {string} content - The text content that may contain URLs
 * @param {boolean} addLineBreaks - Whether to replace newlines with <br> tags (default: true)
 * @returns {JSX.Element|string} - React element with linkified content or empty string
 */
export const renderLinkifiedContent = (content, addLineBreaks = true) => {
  if (!content) return "";

  const linkifiedHtml = linkifyText(content, addLineBreaks);

  // For React components, return a div with dangerously set HTML
  return <div dangerouslySetInnerHTML={{ __html: linkifiedHtml.html }} />;
};
